import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormControl } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  finalize,
  forkJoin,
  Observable,
  retry,
  switchMap,
  throwError,
  timer,
} from 'rxjs';
import { ActivityService } from '../../services/activity-services';
import { SpinnerServiceService } from 'src/app/modules/shared/serviceSpinner/spinner-service.service';
import { TableUtil } from 'src/app/utils/TableExport/TableUtili';
import { BreadcrumbItem } from 'src/app/modules/shared/components/breadcrumb/breadcrumb.component';
import { Activity } from 'src/app/model/Avtivity';
import { ActivityType } from 'src/app/model/activityType';

// ===== REFONTE : feuille de présence d'une activité =====
// Mêmes appels au back qu'avant (liste « finance » des présents, génération du PDF Alfresco).
// Ce qui change :
// - l'activité est celle de l'URL (plus de liste déroulante : le PDF et la liste concordent toujours) ;
// - en-tête de l'activité, compteurs (présents, femmes, hommes, sans téléphone), filtre par genre ;
// - colonnes Adresse et « Présent à » (horodatage) ; plus de popup « œil » (ses champs n'étaient pas renvoyés) ;
// - export Excel avec des colonnes lisibles ; PDF affiché dans une fenêtre au lieu d'un nouvel onglet.

/** Une ligne renvoyée par /activity/getAllPresentParticipantsFinance/{id} (ParticipantReponseFinance) */
interface ParticipantFinance {
  id: string;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
  phoneNumberOne?: string | null;
  gender?: { id?: number | null } | null;
  address?: string | null;
  horodatage?: string | null; // "dd-MM-yyyy HH:mm"
}

type FiltreGenre = 'tous' | 'femmes' | 'hommes';

/** GET /activity/{id} renvoie l'entité du back : elle contient aussi « country », absent du modèle front */
type ActiviteDetails = Activity & {
  country?: { name?: string | null; flag?: string | null } | null;
};

@Component({
  selector: 'app-list-activity-participants-finance',
  templateUrl: './list-activity-participants-finance.component.html',
  styleUrls: ['./list-activity-participants-finance.component.css'],
})
export class ListActivityParticipantsFinanceComponent
  implements OnInit, OnDestroy
{
  idActivity!: number;
  activity: ActiviteDetails | null = null;
  participants: ParticipantFinance[] = [];
  chargement = true;
  breadcrumbItems: BreadcrumbItem[] = [];

  searchCtrl!: FormControl<string | null>;
  filtre: FiltreGenre = 'tous';
  currentPage = 1;
  readonly itemsPerPage = 20;

  erreur: string | null = null;

  // Aperçu du PDF
  generationPdf = false;
  pdfUrl: SafeResourceUrl | null = null;
  private pdfObjectUrl: string | null = null;
  @ViewChild('cadrePdf') cadrePdf?: ElementRef<HTMLIFrameElement>;

  constructor(
    private activityService: ActivityService,
    private spinnerService: SpinnerServiceService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.idActivity = Number(this.route.snapshot.params['id']);
    this.searchCtrl = this.formBuilder.control('');
    this.searchCtrl.valueChanges.subscribe(() => (this.currentPage = 1));
    this.breadcrumbItems = [
      { label: 'OUTILS', url: '' },
      { label: 'ACTIVITES', url: '/dashboard/agenda' },
      {
        label: 'FEUILLE DE PRÉSENCE',
        url: '/dashboard/agenda/participants/' + this.idActivity,
      },
    ];
    this.charger();
  }

  ngOnDestroy(): void {
    this.libererPdf();
  }

  // ---------------------------------------------------------------- chargement
  private charger(): void {
    this.chargement = true;
    this.spinnerService.showSpinner();
    forkJoin({
      activite: this.activityService.getActivityById(this.idActivity),
      liste: this.activityService.listParticipantsPresentFinance(
        this.idActivity,
      ),
    })
      .pipe(
        finalize(() => {
          this.chargement = false;
          this.spinnerService.hideSpinner();
        }),
      )
      .subscribe({
        next: ({ activite, liste }) => {
          this.activity = activite;
          this.participants = Array.isArray(liste) ? liste : [];
        },
        error: () =>
          (this.erreur =
            'Impossible de charger la feuille de présence. Recharge la page dans un instant.'),
      });
  }

  // ---------------------------------------------------------------- en-tête
  get libelleType(): string {
    const valeur = String(this.activity?.activityType ?? '');
    const trouve = (ActivityType.TypesActivity as any[])?.find(
      (t) => t.value === valeur,
    );
    return trouve ? trouve.name : valeur;
  }

  /** "sn" ou "bj" ; les anciennes activités sans pays sont au Sénégal */
  get codePays(): 'sn' | 'bj' {
    const flag =
      this.activity?.country?.flag ?? this.activity?.countryFlag ?? 'sn';
    return String(flag).toLowerCase() === 'bj' ? 'bj' : 'sn';
  }

  get libellePays(): string {
    return (
      this.activity?.country?.name ||
      (this.codePays === 'bj' ? 'Bénin' : 'Sénégal')
    );
  }

  get estFermee(): boolean {
    return this.activity?.isClosed === true;
  }

  // ---------------------------------------------------------------- genre et compteurs
  /** Même codes qu'avant : 1 = masculin, 2 = féminin */
  genre(p: ParticipantFinance): 'F' | 'H' | null {
    const id = p.gender?.id;
    return id === 2 ? 'F' : id === 1 ? 'H' : null;
  }

  libelleGenre(p: ParticipantFinance): string {
    const g = this.genre(p);
    return g === 'F' ? 'Femme' : g === 'H' ? 'Homme' : 'Non renseigné';
  }

  get nbPresents(): number {
    return this.participants.length;
  }

  get nbFemmes(): number {
    return this.participants.filter((p) => this.genre(p) === 'F').length;
  }

  get nbHommes(): number {
    return this.participants.filter((p) => this.genre(p) === 'H').length;
  }

  /** Utile pour les paiements : sans numéro, pas de paiement Wave possible */
  get nbSansTelephone(): number {
    return this.participants.filter((p) => !this.telephone(p)).length;
  }

  pourcentage(n: number): number {
    return this.nbPresents ? Math.round((n * 100) / this.nbPresents) : 0;
  }

  // ---------------------------------------------------------------- affichage d'une ligne
  nomComplet(p: ParticipantFinance): string {
    return [p.firstname, p.lastname]
      .map((v) => (v ?? '').trim())
      .filter((v) => v)
      .join(' ');
  }

  telephone(p: ParticipantFinance): string {
    return (p.phoneNumberOne ?? '').trim();
  }

  /** "30-10-2026 09:12" → { jour: "30-10-2026", heure: "09:12" } */
  horodatage(p: ParticipantFinance): { jour: string; heure: string } | null {
    const valeur = (p.horodatage ?? '').trim();
    if (!valeur) return null;
    const [jour, heure] = valeur.split(' ');
    return { jour: jour ?? '', heure: heure ?? '' };
  }

  // ---------------------------------------------------------------- filtre, recherche, pagination
  choisirFiltre(filtre: FiltreGenre): void {
    this.filtre = filtre;
    this.currentPage = 1;
  }

  get participantsFiltres(): ParticipantFinance[] {
    const texte = (this.searchCtrl?.value ?? '').trim().toLowerCase();
    return this.participants.filter((p) => {
      if (this.filtre === 'femmes' && this.genre(p) !== 'F') return false;
      if (this.filtre === 'hommes' && this.genre(p) !== 'H') return false;
      if (!texte) return true;
      return [
        p.firstname,
        p.lastname,
        this.nomComplet(p),
        p.email,
        p.id,
        p.phoneNumberOne,
        p.address,
        this.libelleGenre(p), // ← AJOUT : « homme », « femme », « non renseigné »
        p.horodatage, // ← AJOUT : « 03-11-2026 », « 09:12 »
      ].some((v) => typeof v === 'string' && v.toLowerCase().includes(texte));
    });
  }

  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(this.participantsFiltres.length / this.itemsPerPage),
    );
  }

  get pageCourante(): ParticipantFinance[] {
    const debut = (this.currentPage - 1) * this.itemsPerPage;
    return this.participantsFiltres.slice(debut, debut + this.itemsPerPage);
  }

  get premiereLigne(): number {
    return this.participantsFiltres.length
      ? (this.currentPage - 1) * this.itemsPerPage + 1
      : 0;
  }

  get derniereLigne(): number {
    return Math.min(
      this.currentPage * this.itemsPerPage,
      this.participantsFiltres.length,
    );
  }

  previousPage(): void {
    if (this.currentPage > 1) this.currentPage--;
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) this.currentPage++;
  }

  // ---------------------------------------------------------------- export Excel
  /** Lignes affichées (filtre et recherche compris), avec des colonnes lisibles */
  exportTable(): void {
    const lignes = this.participantsFiltres.map((p) => ({
      Identifiant: p.id,
      Prénom: p.firstname ?? '',
      Nom: p.lastname ?? '',
      Genre: this.libelleGenre(p),
      Téléphone: this.telephone(p),
      Email: p.email ?? '',
      Adresse: p.address ?? '',
      'Présent le': p.horodatage ?? '',
    }));
    TableUtil.exportArrayToExcel(lignes, this.nomExport());
  }

  /** Excel refuse les caractères : \ / ? * [ ] dans un nom de feuille, et le limite à 31 caractères */
  private nomExport(): string {
    const propre = String(this.activity?.nomActivity ?? '')
      .replace(/[:\\/?*\[\]]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 31)
      .trim();
    return propre || 'Presences';
  }

  // ---------------------------------------------------------------- PDF : génération puis aperçu
  exportPDF(): void {
    if (this.generationPdf) return;
    this.generationPdf = true;
    this.erreur = null;
    this.activityService
      .exportPdfListFinance(this.idActivity)
      .pipe(
        switchMap((noeud: { value?: string } | null) =>
          noeud?.value
            ? this.contenuPdf(noeud.value)
            : throwError(() => new Error('Identifiant du document manquant')),
        ),
        finalize(() => (this.generationPdf = false)),
      )
      .subscribe({
        next: (pdf: Blob) => this.afficherPdf(pdf),
        error: () =>
          (this.erreur =
            "Le PDF n'a pas pu être généré. Réessaie dans un instant."),
      });
  }

  /**
   * Alfresco peut mettre quelques secondes à produire la version PDF :
   * on réessaie jusqu'à 5 fois, à 2 secondes d'intervalle.
   */
  private contenuPdf(idNoeud: string): Observable<Blob> {
    return this.activityService.contenuRenditionPdf(idNoeud).pipe(
      switchMap((pdf: Blob) =>
        pdf && pdf.size > 0 ? [pdf] : throwError(() => new Error('PDF vide')),
      ),
      retry({ count: 5, delay: () => timer(2000) }),
    );
  }

  private afficherPdf(pdf: Blob): void {
    this.libererPdf();
    const fichier =
      pdf.type === 'application/pdf'
        ? pdf
        : new Blob([pdf], { type: 'application/pdf' });
    this.pdfObjectUrl = URL.createObjectURL(fichier);
    // ===== MODIF : sans la barre du lecteur de Chrome =====
    // Elle affichait l'identifiant technique du fichier (ex. « 811a1c1e-… ») au lieu de son nom,
    // et son propre bouton Télécharger reprenait ce nom. Imprimer et Télécharger restent
    // dans l'en-tête de la fenêtre ; la vignette des pages est aussi masquée.
    this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `${this.pdfObjectUrl}#toolbar=0&navpanes=0&view=FitH`,
    );
    // ===== FIN MODIF =====
  }

  imprimerPdf(): void {
    this.cadrePdf?.nativeElement.contentWindow?.print();
  }

  telechargerPdf(): void {
    if (!this.pdfObjectUrl) return;
    const lien = document.createElement('a');
    lien.href = this.pdfObjectUrl;
    lien.download = this.nomFichierPdf();
    // Le lien doit être dans la page pour que le navigateur respecte le nom du fichier
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
  }

  /** Le fichier porte le nom de l'activité, sans les caractères interdits dans un nom de fichier */
  private nomFichierPdf(): string {
    const nom = String(this.activity?.nomActivity ?? '')
      .replace(/[\\/:*?"<>|]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return `${nom || 'Feuille de presence ' + this.idActivity}.pdf`;
  }

  fermerPdf(): void {
    this.libererPdf();
  }

  /** Libère le fichier en mémoire */
  private libererPdf(): void {
    if (this.pdfObjectUrl) {
      URL.revokeObjectURL(this.pdfObjectUrl);
    }
    this.pdfObjectUrl = null;
    this.pdfUrl = null;
  }

  @HostListener('document:keydown.escape')
  fermerAvecEchap(): void {
    if (this.pdfUrl) this.fermerPdf();
  }
}
// ===== FIN REFONTE =====
